import { DatePipe, registerLocaleData } from '@angular/common';
import locale from '@angular/common/locales/en';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { SpinnerLoadingIndicatorComponent } from '@cbre/components';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { isExceeded } from '@business-rules/common/area-validations';
import { CBRE_ALERTS_TRANSLATION_FUNCTION, CbreAlertsModule } from '@cbre/alerts';
import { FEATURE_TOGGLER, FeatureTogglesModule, HybridFeatureTogglerService } from '@cbre/feature-toggles';
import { CbreHttpConfig, CbreHttpModule } from '@cbre/http';
import { CbreLoggerModule, CbreLoggerToken, ConsoleTransporter, TransporterToken } from '@cbre/logger';
import { ReportExportViewModule } from '@miq/report-export-view';
import { LoggedInService, NOT_IN_PLATFORM_USER } from '@cbre/sso';
import { MiqSecuredResourcesModule, TRANSLATOR } from '@miq/utils';
import { CoreModule } from '@core/core.module';
import { dataDogFactory } from '@core/services/datadog-loader.service';
import { environment } from '@env/environment';
import { MiqCatalogModule } from '@miq/catalog';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsModule } from '@ngxs/store';
import { CompanyAddressesPipe } from '@reports/report-details/views/summary-view/company-addresses.pipe';
import { DataCellFormatterPipe } from '@shared/components/tree-data-table/tree-data-row/data-cell-formatter.pipe';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { AppStorageService, REQUEST_SENT } from '@shared/services/app-storage.service';
import { L10N_LOCALE, L10nIntlModule, L10nTranslationModule, L10nTranslationService } from 'angular-l10n';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MaintenanceBannerComponent } from '@shared/components/maintenance-banner/maintenance-banner.component';
import { HttpTranslationLoader } from './l10n/http-translation-loader.service';
import { l10nConfig } from './l10n/l10n-config';
import { MiqTranslationService } from './l10n/miq-translation.service';
import { TranslationFunctionFactory } from './l10n/translate-fn.factory';
import { StackingPlanModule } from '@miq/stacking-plan';
import { ConfidentialityDisablerService } from '@miq/domain';
import { of } from 'rxjs';
import { ReportGeneratorComponent } from './report-generator/report-generator.component';
import { PropertyUsageTypeService } from '@shared/services/property-usage-type.service';
/**
 * CbreHttpOptions configuration
 */
const cbreHttpConfig: CbreHttpConfig = {
  baseUrl: environment.baseUrl,
  // do not intercept logging on next url patterns
  skipUrlInterception: ['assets/', 'cbre/apimanager'],
};

/**
 * Stacking Plan OverlayUtils...
 */
const overlayUtils = { isExceeded };

/**
 * Stacking Plan PV Side By Side Templates
 */
const PVSideBySideTemplates = {
  ...environment.PvSideBySideTemplates,
  EnvId: environment.TemplateHubEnv,
};

/**
 * Console Transporter Factory
 */
export function MiqConsoleTransporterFactory() {
  // template is optional for console transporter
  return new ConsoleTransporter(null, {
    level: environment.logLevel,
  });
}

export function checkForUserInPlatform(storage: AppStorageService) {
  return () => {
    const notInPlatformUser = storage.getItem(NOT_IN_PLATFORM_USER);
    if (notInPlatformUser != null) {
      // MIQ-21449 - Keep userid_token and expires_in to prevent login loop
      storage.clearExcept([REQUEST_SENT, 'userInfo', 'expires_in']);
    }
  };
}

export function initializeFeatureFlags(authService: LoggedInService, featureFlags: HybridFeatureTogglerService) {
  return () => {
    // Return a function
    if (authService.isLoggedIn()) {
      return featureFlags.loadFeatureFlags?.()?.subscribe?.() ?? of(undefined); // Return the Observable
    } else {
      return of(undefined); // Return an Observable that completes immediately
    }
  };
}

export function initializePropertyUsageTypes(
  authService: LoggedInService,
  propertyUsageTypeService: PropertyUsageTypeService
) {
  return () => {
    if (authService.isLoggedIn()) {
      propertyUsageTypeService.getPropertyTypes().subscribe();
    }
  };
}

registerLocaleData(locale);

@NgModule({
  declarations: [AppComponent],
  imports: [
    MaintenanceBannerComponent,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    SpinnerLoadingIndicatorComponent,
    ReactiveFormsModule,
    FormsModule,
    FeatureTogglesModule.forHybrid({
      featureConfig: environment.featuresConfig,
      apiConfig: { apiBaseUrl: '/api/v1/feature-flags' },
    }),
    CoreModule,
    /**
     * For now, only localization to be imported is {@link L10nTranslationModule} as,
     * internally it imports & exports the TranslationModule.
     * Using the `forRoot` static method will import all the required providers for
     * the TranslationModule to work.
     *
     * - For more info on L10nTranslationModule click
     *   {@link https://github.com/robisim74/angular-l10n here}
     * - For more info on TranslationModule or the providers list included by using
     *   the `forRoot` method, click {@link https://github.com/robisim74/angular-l10n here}
     */

    L10nTranslationModule.forRoot(l10nConfig, { translationLoader: HttpTranslationLoader }),
    L10nIntlModule,
    /*
     * Configure Cbre Logger module
     */
    CbreLoggerModule.forRoot({ level: environment.logLevel }),
    CbreAlertsModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    NgxsModule.forRoot([], { developmentMode: false }),
    NgxsRouterPluginModule.forRoot(),
    ReportExportViewModule.forRoot(
      {
        baseUrl: environment.baseUrl,
        s3BucketImagesUrl: environment.media.thumbLocationURI,
        edpMultimediaURI: environment.media.edpMultimediaKey,
      },
      UserInfoResolver
    ),
    MiqSecuredResourcesModule.forRoot({
      buildingPhName: environment.appConstants.BUILDING_PH_IMAGE,
      s3Path: environment.media.originalLocationURI,
    }),
    StackingPlanModule.forRoot(
      { baseUrl: environment.baseUrl, media: environment.media },
      overlayUtils,
      UserInfoResolver,
      PVSideBySideTemplates
    ),
    CbreHttpModule.forRoot(cbreHttpConfig),
    MiqCatalogModule,
    /** NgxReduxDevtoolsPluginModule needs to be the last module imported always */
    NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
    /** NgxReduxDevtoolsPluginModule needs to be the last module imported always */
    ReportGeneratorComponent,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: checkForUserInPlatform,
      deps: [AppStorageService],
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'en_US' },
    {
      provide: CBRE_ALERTS_TRANSLATION_FUNCTION,
      useFactory: TranslationFunctionFactory,
      deps: [L10nTranslationService],
    },
    { provide: TRANSLATOR, useClass: MiqTranslationService, deps: [L10nTranslationService, L10N_LOCALE] },
    /*
     * Material providers
     */
    /*
     * Only for qa or prod
     */
    { provide: APP_INITIALIZER, useFactory: dataDogFactory, deps: [CbreLoggerToken, UserInfoResolver], multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFeatureFlags,
      deps: [LoggedInService, FEATURE_TOGGLER],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializePropertyUsageTypes,
      deps: [LoggedInService, PropertyUsageTypeService],
      multi: true,
    },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
    providePrimeNG({ theme: { preset: Aura, options: { darkModeSelector: false } } }),
    { provide: MatDialogRef, useValue: {} },
    /*
     * Configure Log Transporters
     */
    {
      provide: TransporterToken,
      useFactory: MiqConsoleTransporterFactory,
      multi: true,
    },
    // todo cleanup these pipe, try to include it in report module
    DatePipe,
    DataCellFormatterPipe,
    CompanyAddressesPipe,
    ConfidentialityDisablerService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
